import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Calculator, 
  LogOut, 
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Shield
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSS = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rates, setRates] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [formData, setFormData] = useState({
    type: "employee",
    description: "",
    employee_rate: 0,
    employer_rate: 0,
    self_employed_rate: 0,
    minimum_base: 820,
    maximum_base: 0,
    year: 2024
  });

  useEffect(() => {
    loadRates();
  }, []);

  const loadRates = async () => {
    try {
      const response = await axios.get(`${API}/ss`, { headers: getAuthHeaders() });
      setRates(response.data);
    } catch (error) {
      toast.error("Erro ao carregar taxas SS");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const openNewDialog = () => {
    setEditingRate(null);
    setFormData({
      type: "employee",
      description: "",
      employee_rate: 0,
      employer_rate: 0,
      self_employed_rate: 0,
      minimum_base: 820,
      maximum_base: 0,
      year: 2024
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (rate) => {
    setEditingRate(rate);
    setFormData({
      type: rate.type,
      description: rate.description,
      employee_rate: rate.employee_rate,
      employer_rate: rate.employer_rate,
      self_employed_rate: rate.self_employed_rate,
      minimum_base: rate.minimum_base,
      maximum_base: rate.maximum_base,
      year: rate.year
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        employee_rate: parseFloat(formData.employee_rate) || 0,
        employer_rate: parseFloat(formData.employer_rate) || 0,
        self_employed_rate: parseFloat(formData.self_employed_rate) || 0,
        minimum_base: parseFloat(formData.minimum_base) || 0,
        maximum_base: parseFloat(formData.maximum_base) || 0
      };

      if (editingRate) {
        await axios.put(`${API}/ss/${editingRate.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Taxa atualizada com sucesso!");
      } else {
        await axios.post(`${API}/ss`, payload, { headers: getAuthHeaders() });
        toast.success("Taxa criada com sucesso!");
      }
      setIsDialogOpen(false);
      loadRates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar taxa");
    }
  };

  const handleDelete = async (rateId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar esta taxa?")) return;
    try {
      await axios.delete(`${API}/ss/${rateId}`, { headers: getAuthHeaders() });
      toast.success("Taxa eliminada");
      loadRates();
    } catch (error) {
      toast.error("Erro ao eliminar taxa");
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      employee: "Trabalhador por Conta de Outrem",
      green_receipts: "Recibos Verdes",
      b2b: "B2B / Gerente Empresa"
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-ss-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-manrope font-bold text-lg text-foreground">Evolutt</span>
              <p className="text-xs text-muted-foreground">Segurança Social</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Back & Title */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="sm" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div>
              <h1 className="font-manrope font-extrabold text-2xl text-foreground">Segurança Social</h1>
              <p className="text-muted-foreground">Gerir taxas de contribuição</p>
            </div>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-brand-600 text-white hover:bg-brand-700 font-bold"
                onClick={openNewDialog}
                data-testid="add-rate-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nova Taxa
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold text-xl">
                  {editingRate ? "Editar Taxa SS" : "Nova Taxa SS"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Ano</Label>
                    <Input
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                      data-testid="year-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                      <SelectTrigger data-testid="type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">Trabalhador por Conta de Outrem</SelectItem>
                        <SelectItem value="green_receipts">Recibos Verdes</SelectItem>
                        <SelectItem value="b2b">B2B / Gerente Empresa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Descrição</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Ex: Taxa SS - Trabalhador por Conta de Outrem"
                    data-testid="description-input"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Taxa Empregado (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.employee_rate}
                      onChange={(e) => setFormData({ ...formData, employee_rate: e.target.value })}
                      className="font-mono"
                      data-testid="employee-rate-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Taxa Entidade (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.employer_rate}
                      onChange={(e) => setFormData({ ...formData, employer_rate: e.target.value })}
                      className="font-mono"
                      data-testid="employer-rate-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Taxa Indep. (Recibos/B2B) (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.self_employed_rate}
                      onChange={(e) => setFormData({ ...formData, self_employed_rate: e.target.value })}
                      className="font-mono"
                      data-testid="self-employed-rate-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Base Mínima (€)</Label>
                    <Input
                      type="number"
                      value={formData.minimum_base}
                      onChange={(e) => setFormData({ ...formData, minimum_base: e.target.value })}
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Base Máxima (€)</Label>
                    <Input
                      type="number"
                      value={formData.maximum_base}
                      onChange={(e) => setFormData({ ...formData, maximum_base: e.target.value })}
                      placeholder="0 = Sem limite"
                      className="font-mono"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-primary text-primary-foreground" data-testid="save-btn">
                    {editingRate ? "Guardar Alterações" : "Criar Taxa"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Rates List */}
        <Card className="bg-white border border-border/60 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : rates.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Shield className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nenhuma taxa SS configurada</p>
                <p className="text-sm text-muted-foreground">Clique em "Nova Taxa" ou carregue os dados iniciais</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Ano</TableHead>
                    <TableHead className="font-semibold">Tipo</TableHead>
                    <TableHead className="font-semibold">Empregado</TableHead>
                    <TableHead className="font-semibold">Entidade</TableHead>
                    <TableHead className="font-semibold">Indep. (Recibos/B2B)</TableHead>
                    <TableHead className="font-semibold text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rates.map((rate) => (
                    <TableRow key={rate.id} data-testid={`rate-row-${rate.id}`}>
                      <TableCell className="font-mono font-medium">{rate.year}</TableCell>
                      <TableCell>{getTypeLabel(rate.type)}</TableCell>
                      <TableCell className="font-mono">{rate.employee_rate}%</TableCell>
                      <TableCell className="font-mono">{rate.employer_rate}%</TableCell>
                      <TableCell className="font-mono">{rate.self_employed_rate}%</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEditDialog(rate)}
                            data-testid={`edit-btn-${rate.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDelete(rate.id)}
                            data-testid={`delete-btn-${rate.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminSS;
